import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import TopUp, { TOPUP_CSS } from './TopUp';
import Icon from './Icons';
import CastPick, { CASTPICK_CSS } from './CastPick';

/**
 * "Hear this script" for an analysis that has NO read yet (old analyses). ONE decision: record the
 * whole script (what it costs is on the button) or just the free first scenes. Nothing is charged
 * here: a credit is taken only when the audio is ready.
 *
 *   GET  {base}/offer   what this viewer gets, what it costs, their balance
 *   POST {base}/start   render what is free for this tier; { full: true } orders the whole script;
 *                       { cast: {...} } = the picks from "Choose the main voices" (CastPick.jsx)
 *
 * Two steps (owner, 2026-10-11): first WHAT to record (whole script / first scenes); only then HOW to
 * cast it — "Cast automatically and start" or "Choose the main voices" (then Record).
 *
 * Short of credits: the button becomes ONE action, "Buy credits & record whole script". It opens the
 * packs right under it; Stripe returns to ?trstart=<hash>&trfull=1 (tableReadApp.js), which reopens
 * this dialog with `autoFull`, and the recording starts by itself once the balance covers it — the
 * buyer already chose "record the whole script" by buying.
 */
const num = (n) => String(Number(Number(n).toFixed(1)));

export default function StartRead({ base, title, onStarted, onListen, onClose, autoFull = false }) {
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [topup, setTopup] = useState(false);
  const topRef = useRef(null);
  const fired = useRef(false);
  const closeRef = useRef(onClose);
  // Step 2: {full} once they chose what to record; choosing = the cast picker is open. Picks are kept
  // for this tab, so a Stripe round trip (buy credits -> come back) does not lose them.
  const castKey = `tr-cast:${base}`;
  const [step, setStep] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [castStatus, setCastStatus] = useState('casting');      // CastPick: casting | ready | error
  const [started, setStarted] = useState(null);                 // {at, whole, cast} once /start succeeded
  closeRef.current = started ? onStarted : onClose;
  const [prog, setProg] = useState(null);                       // /scenes progress {ready, total, building}
  const firstReadyAt = useRef(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!started) return undefined;
    let live = true, timer = null;
    const poll = () => axios.get(`${base}/scenes`).then((r) => {
      if (!live) return;
      const p = r.data.progress;
      if (p) { setProg(p); if (p.ready > 0 && !firstReadyAt.current) firstReadyAt.current = Date.now(); }
      if (!p || p.building) timer = setTimeout(poll, 8000);
    }).catch(() => { if (live) timer = setTimeout(poll, 15000); });
    poll();
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => { live = false; clearTimeout(timer); clearInterval(t); };
  }, [started, base]);
  const [cast, setCast] = useState(() => { try { return JSON.parse(sessionStorage.getItem(castKey) || '{}'); } catch (_) { return {}; } });
  const setCastSaved = (c) => { setCast(c); try { sessionStorage.setItem(castKey, JSON.stringify(c)); } catch (_) {} };

  useEffect(() => {
    axios.get(`${base}/offer`).then((r) => setO(r.data)).catch((e) => setErr(e?.response?.data?.error ?? e.message));
  }, [base]);
  // Once recording started, closing (backdrop / Esc) is "keep recording in the background".
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, []);
  useEffect(() => { if (topup) topRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [topup]);

  const go = (full = false, withCast = false) => {
    setBusy(true); setErr(null);
    const body = { ...(full ? { full: true } : {}), ...(withCast && Object.keys(cast).length ? { cast } : {}) };
    axios.post(`${base}/start`, body).then(() => {
      try { sessionStorage.removeItem(castKey); } catch (_) {}
      // Stay open as the progress view (owner, 2026-10-11): what to expect and how long, live.
      setBusy(false); setStarted({ at: Date.now(), whole: full || !!o?.full_free, cast: withCast && castStatus === 'ready' });
    })
      .catch((e) => { setBusy(false); setErr(e?.response?.data?.error ?? e.message); });
  };

  const resumeUrl = (() => {
    if (typeof location === 'undefined' || !o?.topup?.hash) return null;
    const u = new URL(location.href); u.searchParams.set('trstart', o.topup.hash); u.searchParams.set('trfull', '1'); return u.toString();
  })();
  const canFull = !!o?.can_buy_full;
  const balance = o ? Number(o.credits.balance) : 0;
  const short = canFull && !o.admin_free && balance < o.full_cost;
  // The Android app sells nothing (the server sends no top-up there): short means "add credits on the website".
  const canBuyHere = !!o?.topup?.checkout_url;
  // Back from a purchase made here: the order was "buy and record the whole script" — carry it out.
  // Back from a purchase made here: they asked for the whole script — go straight to how to cast it.
  useEffect(() => {
    if (autoFull && o && canFull && o.can_start && !short && !fired.current) {
      fired.current = true; setStep({ full: true }); if (Object.keys(cast).length) setChoosing(true);
    }
  }, [autoFull, o]);  // eslint-disable-line react-hooks/exhaustive-deps
  const showBal = !!o && !o.admin_free && !o.full_free && o.sellable;
  const wholeStep = !!step && (step.full || !!o?.full_free);          // Deep Focus: "Record this script" is the whole read
  const plain = !o ? null
    : o.full_free ? `The whole script is included with your Deep Focus analysis — about ${o.minutes} minutes.`
    : o.sellable ? `The first ${o.free_scenes} scenes are free. The rest is ${o.credits.scene} credit a scene, or ${o.credits.full} credits for all of it.`
    : `The first ${o.free_scenes} scenes are free. The full read comes with a Deep Focus analysis.`;

  return (
    <div className="tr-fb-backdrop" onClick={() => closeRef.current()}>
      <style>{CSS + TOPUP_CSS + CASTPICK_CSS}</style>
      <div className="tr-fb" role="dialog" aria-modal="true" aria-labelledby="tr-st-h" onClick={(e) => e.stopPropagation()}>
        <div className="tr-fb-head">
          <span className="tr-badge"><Icon name="headphones" size={22} /></span>
          <h2 id="tr-st-h" className="tr-fb-h">Hear {title || 'this script'} read aloud</h2>
          {showBal && (
            <span className={short ? 'tr-chip tr-chipshort' : 'tr-chip'} title={short ? `${num(o.full_cost - balance)} more needed for the whole script` : 'Your craft credits'}>
              <Icon name="coin" size={16} /><b>{num(balance)}</b> credits
            </span>
          )}
        </div>
        <p className="tr-dim">
          An AI table read with a voice for every character. It is an early feature — expect the odd mispronunciation.
        </p>
        {started && <Progress started={started} prog={prog} firstReadyAt={firstReadyAt.current} o={o}
                              onListen={onListen} onDone={onStarted} tick={tick} />}
        {!started && o && !canFull && <p className="tr-dim">{plain}</p>}
        {err && <p className="tr-err">{err}</p>}
        {!o && !err && <p className="tr-dim">Checking…</p>}

        {!started && step && (
          <div className="tr-step">
            <button className="tr-linkbtn tr-backlink" onClick={() => (choosing ? setChoosing(false) : setStep(null))} disabled={busy}>← Back</button>
            <p className="tr-steph">{wholeStep ? `Whole script — ${o.full_free || o.admin_free ? 'included' : `${o.full_cost} credits`}` : `First ${o.free_scenes} scenes — free`}</p>
            {!choosing ? (
              <>
                <p className="tr-dim tr-q">How should the voices be cast?</p>
                <div className="tr-choices">
                  <button className="tr-choice tr-choice-main" onClick={() => go(step.full)} disabled={busy}>
                    <Icon name="mic" size={22} />
                    <span className="tr-choice-text">
                      <b>{busy ? 'Starting…' : 'Cast automatically and start'}</b>
                      <span>A voice for every character, picked from the script. You can change voices later.</span>
                    </span>
                    <Icon name="chevron" size={20} />
                  </button>
                  <button className="tr-choice" onClick={() => setChoosing(true)} disabled={busy}>
                    <Icon name="users" size={22} />
                    <span className="tr-choice-text">
                      <b>Choose the main voices</b>
                      <span>Listen to the voices and pick them for the characters with the most lines.</span>
                    </span>
                    <Icon name="chevron" size={20} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <CastPick base={base} value={cast} onChange={setCastSaved} onStatus={setCastStatus} />
                {castStatus !== 'ready' ? (
                  <p className="tr-skip"><button className="tr-linkbtn" onClick={() => go(step.full)} disabled={busy}>
                    {busy ? 'Starting…' : 'Don\'t want to wait? Cast automatically and start now'}</button></p>
                ) : <div className="tr-fb-actions">
                  <button className="tr-btn tr-play" onClick={() => go(step.full, true)} disabled={busy}>
                    <span className="tr-lab"><Icon name="mic" />{busy ? 'Starting…' : wholeStep ? 'Record whole script' : `Record first ${o.free_scenes} scenes`}</span>
                    <span className="tr-cost">{!wholeStep || o.full_free || o.admin_free ? (wholeStep ? 'included' : '0 credits') : `${o.full_cost} credits`}</span>
                  </button>
                </div>}
              </>
            )}
          </div>
        )}

        {!started && !step && <div className="tr-choices">
          {canFull ? (
            <>
              <button className="tr-choice tr-choice-main" disabled={busy || !o?.can_start || (short && !canBuyHere)}
                      onClick={() => (short ? setTopup(true) : setStep({ full: true }))}>
                <Icon name={short ? 'plus' : 'mic'} size={22} />
                <span className="tr-choice-text">
                  <b className="tr-choice-title"><span>{short && canBuyHere ? 'Buy credits & record whole script' : 'Record whole script'}</span>
                    <span className="tr-cost">{o.admin_free ? 'free' : `${o.full_cost} credits`}</span></b>
                  <span>{o.scenes} scenes · about {o.minutes} min{o.admin_free ? '' : ' · charged when ready'}</span>
                </span>
                <Icon name="chevron" size={20} />
              </button>
              <button className="tr-choice" onClick={() => setStep({ full: false })} disabled={busy || !o?.can_start}>
                <Icon name="mic" size={22} />
                <span className="tr-choice-text">
                  <b className="tr-choice-title"><span>Record first {o.free_scenes} scenes</span><span className="tr-cost">0 credits</span></b>
                  <span>Hear how it sounds before you record the rest.</span>
                </span>
                <Icon name="chevron" size={20} />
              </button>
            </>
          ) : (
            <button className="tr-choice tr-choice-main" onClick={() => setStep({ full: false })} disabled={busy || !o?.can_start}>
              <Icon name="mic" size={22} />
              <span className="tr-choice-text">
                <b>Record this script</b>
                <span>{o?.full_free ? `${o.scenes} scenes · about ${o.minutes} min · included` : `The first ${o?.free_scenes ?? 3} scenes, free`}</span>
              </span>
              <Icon name="chevron" size={20} />
            </button>
          )}
        </div>}

        {!step && short && !canBuyHere && <p className="tr-sub">Add credits on scriptreader.ai to record the whole script.</p>}
        {!step && short && topup && o.topup?.checkout_url && (
          <div ref={topRef}><TopUp topup={o.topup} resume={resumeUrl} heading="Pick a pack — recording starts when you are back" /></div>
        )}
        {!started && <p className="tr-foot"><button className="tr-linkbtn" onClick={onClose}>Not now</button></p>}
      </div>
    </div>
  );
}

/**
 * After /start: the dialog stays open as the progress view. Before any scene is ready the estimate
 * is setup + casting (the AI profile reads the whole script unless the cast was already made) + the
 * first scene; after that the measured pace of THIS read decides "about N min left".
 */
function Progress({ started, prog, firstReadyAt, o, onListen, onDone }) {
  const now = Date.now();
  const mm = (sec) => `${Math.floor(sec / 60)}:${String(Math.max(0, sec % 60)).padStart(2, '0')}`;
  const el = Math.round((now - started.at) / 1000);
  const ready = prog?.ready ?? 0, total = prog?.total ?? (started.whole ? o?.scenes : o?.free_scenes) ?? 0;
  const done = prog && !prog.building && total > 0 && ready >= total;
  let left = null;                                                       // seconds, when we can say
  // Measured pace once there is enough to measure (scenes finish in bursts: two render slots), else
  // the offer's ~0.45 min a scene. Never quicker than 10 s a scene.
  const since = firstReadyAt ? (now - firstReadyAt) / 1000 : 0;
  if (ready >= 5 && since >= 60) left = Math.round(Math.max(10, since / (ready - 1)) * (total - ready));
  else if (ready >= 1) left = Math.round((total - ready) * 27);
  const firstEta = started.cast ? '2–3 minutes' : '3–5 minutes';
  const pct = total ? Math.round((ready / total) * 100) : 0;
  return (
    <div className="tr-prog" role="status" aria-live="polite">
      <div className="tr-prog-head">
        {done ? <span className="tr-prog-ok" aria-hidden="true">✓</span> : <span className="cp-spin" aria-hidden="true" />}
        <b>{done ? 'Your table read is ready' : ready > 0 ? 'Recording — you can listen already' : 'Recording started'}</b>
        <span className="cp-wait-time">{mm(el)}</span>
      </div>
      {!done && ready === 0 && (
        <p>Setting up the cast and recording the first scenes — usually about {firstEta}.
          {started.whole && o?.minutes ? ` The whole script takes about ${o.minutes} minutes.` : ''}</p>
      )}
      {!done && ready > 0 && (
        <p>{ready} of {total} scenes ready{left != null ? ` · about ${Math.max(1, Math.round(left / 60))} min left` : ''}. New scenes appear in the player as they finish.</p>
      )}
      {done && <p>All {total} scenes are recorded.</p>}
      <div className="cp-bar"><i style={{ width: `${done ? 100 : Math.max(ready ? pct : Math.min(8, el / 30), 2)}%` }} /></div>
      <p className="cp-wait-note">You can close this window — recording carries on, and the Listen button on the page appears as soon as scenes are ready.</p>
      <div className="tr-fb-actions">
        {ready > 0 && onListen && (
          <button className="tr-btn tr-play" onClick={onListen}><span className="tr-lab"><Icon name="headphones" />Listen now</span></button>
        )}
        <button className="tr-btn" onClick={onDone}>{ready > 0 ? 'Close' : 'Close — keep recording'}</button>
      </div>
    </div>
  );
}

/* Standalone: this dialog opens on the Blade analysis page, where the player's stylesheet is not
   mounted, so it carries the few rules it needs. */
const CSS = `
.tr-fb-backdrop{position:fixed;inset:0;background:rgba(16,45,56,.38);display:flex;align-items:center;
  justify-content:center;padding:16px;z-index:2147483000;font-family:inherit;color:#14536e;overflow:auto}
.tr-fb{background:#fff;border-radius:12px;max-width:520px;width:100%;padding:22px 24px;box-shadow:0 20px 50px -20px rgba(16,45,56,.5);max-height:calc(100vh - 32px);overflow:auto}
.tr-fb-head{display:flex;align-items:center;gap:12px;margin:0 0 10px;flex-wrap:wrap}
.tr-badge{flex:0 0 auto;width:40px;height:40px;border-radius:50%;background:#e3edf1;color:#14536e;display:inline-flex;align-items:center;justify-content:center}
.tr-fb-h{font-size:1.25rem;font-weight:700;color:#102d38;margin:0;line-height:1.25;flex:1 1 200px;min-width:0}
.tr-chip{flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;border:1px solid #cfdde4;background:#f4f8fa;color:#14536e;
  border-radius:999px;padding:6px 12px;font-size:.9rem;white-space:nowrap}
.tr-chip b{font-size:1.1rem;color:#102d38}
.tr-chipshort{background:#fff4d6;border-color:#e8c46a;color:#7a4e00}
.tr-chipshort b{color:#5c3a00}
.tr-ico{flex:0 0 auto}
.tr-dim{color:#46657a;margin:0 0 12px;line-height:1.5;font-size:.95rem}
.tr-err{color:#a4243b;margin:0 0 10px}
.tr-fb-actions{display:flex;flex-direction:column;gap:8px;margin-top:16px}
.tr-fb-actions .tr-btn{width:100%;justify-content:space-between}
.tr-lab{display:inline-flex;align-items:center;gap:8px}
.tr-cost{flex:0 0 auto;font-size:.88rem;font-weight:700;border-radius:999px;padding:3px 10px;background:#e3edf1;color:#14536e}
.tr-play .tr-cost{background:rgba(255,255,255,.2);color:#fff}
.tr-play:disabled .tr-cost{background:#cfdde4;color:#46657a}
.tr-sub{font-size:.85rem;color:#46657a;margin:-2px 0 6px;display:flex;align-items:center;justify-content:center;gap:6px}
.tr-foot{text-align:center;margin:12px 0 0}
.tr-step{margin-top:6px}
.tr-backlink{font-size:.9rem;padding:2px 0}
.tr-steph{font-weight:700;color:#102d38;margin:8px 0 2px;font-size:1.02rem}
.tr-q{margin:0 0 4px}
.tr-skip{margin:12px 0 0;text-align:center;font-size:.92rem}
.tr-prog{border:1.5px solid #cfdde4;border-radius:12px;padding:14px 16px;margin:10px 0 0;background:#f6fafc}
.tr-prog-head{display:flex;align-items:center;gap:10px;color:#102d38}
.tr-prog-head b{flex:1;font-size:1.02rem}
.tr-prog p{margin:8px 0 0;color:#46657a;font-size:.92rem;line-height:1.45}
.tr-prog .cp-wait-note{font-size:.82rem}
.tr-prog-ok{width:22px;height:22px;border-radius:50%;background:#1f6b45;color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:.8rem;font-weight:700}
.tr-btn{font:inherit;border:1.5px solid #8fb1c2;background:#fff;color:#14536e;border-radius:10px;padding:11px 14px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;
  font-weight:600;box-shadow:0 1px 2px rgba(16,45,56,.14),0 2px 6px -2px rgba(16,45,56,.14);transition:background .12s,box-shadow .12s,transform .06s}
.tr-btn:hover:not(:disabled){border-color:#14536e;background:#f2f7f9;box-shadow:0 2px 4px rgba(16,45,56,.16),0 6px 14px -6px rgba(16,45,56,.3)}
.tr-btn:active:not(:disabled){transform:translateY(1px);box-shadow:0 1px 2px rgba(16,45,56,.14)}
.tr-play{background:#14536e;color:#fff;border-color:#14536e;font-weight:650}
.tr-play:hover:not(:disabled){background:#0f4359;border-color:#0f4359}
.tr-choices{display:flex;flex-direction:column;gap:10px;margin-top:12px}
.tr-choice{font:inherit;text-align:left;display:flex;align-items:center;gap:14px;width:100%;padding:14px 16px;border-radius:12px;cursor:pointer;
  border:1.5px solid #8fb1c2;background:#fff;color:#14536e;box-shadow:0 1px 2px rgba(16,45,56,.14),0 3px 8px -3px rgba(16,45,56,.18);
  transition:background .12s,box-shadow .12s,transform .06s}
.tr-choice:hover:not(:disabled){border-color:#14536e;background:#f2f7f9;box-shadow:0 2px 4px rgba(16,45,56,.16),0 8px 18px -8px rgba(16,45,56,.35)}
.tr-choice:active:not(:disabled){transform:translateY(1px)}
.tr-choice-main{background:#14536e;border-color:#14536e;color:#fff}
.tr-choice-main:hover:not(:disabled){background:#0f4359;border-color:#0f4359}
.tr-choice-text{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}
.tr-choice-text b{font-size:1rem}
.tr-choice-text > span{font-size:.86rem;opacity:.85;line-height:1.35}
.tr-choice:disabled{opacity:.6;cursor:default}
.tr-choice-title{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.tr-choice-title .tr-cost{flex:0 0 auto;margin-top:1px}
.tr-choice-main .tr-cost{background:rgba(255,255,255,.2);color:#fff}
.tr-choice-main:disabled{background:#e3edf1;border-color:#e3edf1;color:#46657a;opacity:1}
.tr-choice-main:disabled .tr-cost{background:#cfdde4;color:#46657a}
@media (max-width:420px){.tr-choice{gap:10px;padding:12px}.tr-choice .tr-cost{font-size:.78rem;padding:2px 8px}}
.tr-play:disabled{background:#e3edf1;border-color:#e3edf1;color:#46657a;cursor:default}
.tr-btn:disabled{cursor:default}
.tr-btn:focus-visible,.tr-linkbtn:focus-visible{outline:2px solid #14536e;outline-offset:2px}
.tr-linkbtn{border:0;background:none;padding:4px;font:inherit;color:#14536e;text-decoration:underline;cursor:pointer}
`;
